/**
 * Shared subscription-order (מחסן והזמנות → הזמנות מנויים) types.
 *
 * An order is a **shipping document**, not a view of a subscription: it is produced by a
 * manual trigger and then frozen. Every field it displays is snapshotted at generation
 * time, so renaming a customer or a catalog item later never rewrites history. That is
 * the opposite of `Subscription`, which joins live data because it shows current state.
 *
 * Orders carry **no prices** — the warehouse packs by quantity.
 *
 * This file is dependency-free (no React/MUI/browser imports) so the backend can import
 * it via the `@malash-office/types/subscription-orders` deep alias.
 */
import { SelectOptionLite } from './customer.types';
import { SubscriptionAddress, SubscriptionTag } from './subscription.types';

const toOptions = (values: readonly string[]): SelectOptionLite[] => values.map(v => ({ label: v, value: v }));

// --- סטטוס הזמנה ---
// As everywhere else in this project, the stored value *is* the Hebrew label, so a row can be
// rendered and exported as-is. Unlike the rest of an order — which is frozen at generation —
// the status is the one field the warehouse edits afterwards, as the order moves along.
export const ORDER_STATUS_NEW = 'הזמנה חדשה';
export const ORDER_STATUS_IN_PROGRESS = 'בטיפול';
export const ORDER_STATUS_COMPLETED = 'הזמנה הושלמה';
export const ORDER_STATUS_CANCELLED = 'הזמנה בוטלה';
export const ORDER_STATUS_OTHER = 'אחר';

export const SUBSCRIPTION_ORDER_STATUSES = [
  ORDER_STATUS_NEW,
  ORDER_STATUS_IN_PROGRESS,
  ORDER_STATUS_COMPLETED,
  ORDER_STATUS_CANCELLED,
  ORDER_STATUS_OTHER
] as const;

export const SUBSCRIPTION_ORDER_STATUS_OPTIONS = toOptions(SUBSCRIPTION_ORDER_STATUSES);

/**
 * The statuses an order may still be edited in.
 *
 * `הזמנה הושלמה` is out because the package is packed and gone — editing it afterwards changes
 * a shipment that already happened and, through `itemsAmount`, what the customer is billed for
 * it. `הזמנה בוטלה` is out because a called-off shipment has nothing left to edit; the way back
 * is to un-cancel it first, which is a visible act.
 */
export const ORDER_EDITABLE_STATUSES: readonly string[] = [
  ORDER_STATUS_NEW,
  ORDER_STATUS_IN_PROGRESS,
  ORDER_STATUS_OTHER
];

export const isOrderEditable = (status: string): boolean => ORDER_EDITABLE_STATUSES.includes(status);

export const ORDER_NOT_EDITABLE_MESSAGE = 'לא ניתן לערוך הזמנה שהושלמה או בוטלה';

/** Said in the edit dialog itself, because it is the one thing about the feature that surprises. */
export const ORDER_EDIT_SCOPE_NOTICE =
  'השינוי חל על ההזמנה של השבוע הזה בלבד ואינו משנה את המנוי. ההפקה הבאה תיווצר לפי המנוי.';

/** Chip colour per status, as **theme palette keys** — this project never hardcodes a colour. */
export const SUBSCRIPTION_ORDER_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  [ORDER_STATUS_NEW]: 'info',
  [ORDER_STATUS_IN_PROGRESS]: 'warning',
  [ORDER_STATUS_COMPLETED]: 'success',
  [ORDER_STATUS_CANCELLED]: 'error',
  [ORDER_STATUS_OTHER]: 'default'
};

/**
 * One packed line: what to pack and how many. The name is a snapshot of the catalog item.
 *
 * The warehouse packs by quantity and is never shown a price, but the line carries one so the
 * monthly billing job can price a past month without re-reading a catalog that may have been
 * re-priced since. The edit dialog is the only screen that displays `unitPrice`.
 */
export interface SubscriptionOrderItem {
  id: number;
  orderId: number;
  /** Null once the catalog item is deleted — `name` is what keeps the line readable. */
  planItemId: number | null;
  name: string;
  quantity: number;
  /** Frozen at generation, or the price someone typed while editing this one shipment. */
  unitPrice: number;
  /** True when the price was set by hand and no longer follows the catalog. */
  priceOverridden: boolean;
}

export interface SubscriptionOrder {
  id: number;
  batchId: number;
  /** 'YYYY-MM-DD' — a shipping date has no time of day. */
  orderDate: string;
  planId: number | null;
  planName: string;
  chabadHouseName: string;
  deliveryMethod: string;
  /**
   * Which carrier takes this package — one of `SHIPPING_COMPANIES`, snapshotted from the
   * subscription at generation time. Null for every method other than משלוח עד הבית, and on
   * orders whose subscription never named one; the warehouse cannot open a shipment then.
   */
  shippingCompany: string | null;
  pickupPointId: string | null;
  /**
   * Set only for `מצטרף למשלוח קיים` — the order this one travels with, frozen from the
   * subscription's link at generation time. Null when the joined subscription produced no order
   * in the same run, in which case this order is simply packed on its own.
   */
  joinsOrderId: number | null;
  /** The joined order's `chabadHouseName`, so a row can name who it travels with. */
  joinsIntoName: string | null;
  address: SubscriptionAddress;
  items: SubscriptionOrderItem[];
  /** One of `SUBSCRIPTION_ORDER_STATUSES`. */
  status: string;
  /** Σ quantity × unitPrice over the lines. Recomputed on every edit; summed by monthly billing. */
  itemsAmount: number;
  /** This shipment's delivery charge — derived from the method, or waived by hand. */
  deliveryCost: number;
  /** True when `deliveryCost` was set by hand, so re-pricing the items leaves it alone. */
  deliveryCostOverridden: boolean;
  /**
   * When this order was last edited by hand, or null if it is exactly as it was generated.
   *
   * An edited order legitimately differs from the subscription behind it. Without this pair
   * that difference is unexplainable, and reads as a generation bug.
   */
  editedAt: string | null;
  editedBy: { id: number; firstName: string; lastName: string } | null;
  /** Kept for traceability only; the display never reads through them. */
  subscriptionId: number | null;
  customerId: number | null;
  createdAt?: string;
}

/** One generation run — the unit an undo reverses. */
export interface SubscriptionOrderBatch {
  id: number;
  planId: number | null;
  planName: string;
  generatedAt: string;
  /** Active subscriptions examined. */
  scannedCount: number;
  /** Orders actually created. */
  orderCount: number;
  /** Subscriptions skipped because none of their items are active in the catalog. */
  skippedCount: number;
  user?: { id: number; firstName: string; lastName: string } | null;
}

/** Query params for the server-side order list (all optional, mirror the URL). */
export interface SubscriptionOrderListParams {
  page?: string | number;
  rowsPerPage?: string | number;
  sortField?: string;
  sortOrder?: string;
  /** Inclusive 'YYYY-MM-DD' bounds on `orderDate`. */
  startDate?: string;
  endDate?: string;
  planId?: string | number;
  deliveryMethod?: string;
  /** Comma-separated — the status filter is a multi-select. */
  status?: string;
  /**
   * Comma-separated tag ids — an order matches when its subscription carries **any** of them.
   * Read live from the subscription, unlike everything else on an order.
   */
  tagId?: string;
  /**
   * Free text over the בית חב"ד, the customer's name / מזהה לקוח / phone, and the order number.
   * Like `tagId`, a match brings its whole join group — the parent and every joiner.
   */
  searchTerm?: string;
  /**
   * Not a screen filter: the credits dialog uses it to list the orders of the shaliach being
   * credited, so a credit can point at the shipment it is about.
   */
  customerId?: string | number;
}

export interface UpdateOrderStatusBody {
  status: string;
}

/**
 * `POST /subscription-orders/bulk/status` — the selection bar's one action.
 *
 * Deliberately the same status set as the row dropdown, `הזמנה הושלמה` included. The warehouse
 * already marks an order complete from the table without passing through תצוגת מחסן, and a rule
 * that exists only in the bulk path would be a surprise rather than a safeguard.
 */
export interface BulkOrderStatusBody {
  ids: number[];
  status: string;
}

export interface BulkOrderStatusResult {
  updated: number;
}

/**
 * The cap on one bulk request. The table's largest page is exactly 1000 rows and "select all"
 * selects the page, so this is the widest selection a human can actually make — anything past it
 * is a script or a client bug, and it should fail at the schema.
 */
export const MAX_BULK_ORDER_STATUS_IDS = 1000;

/** One line of an edited order. Prices are only read when the client says a human set them. */
export interface UpdateOrderItem {
  planItemId: number;
  quantity: number;
  unitPrice?: number;
  priceOverridden?: boolean;
}

/**
 * Editing one shipment — `PUT /subscription-orders/:id`.
 *
 * **This changes the order for its own week only.** Nothing here reaches the subscription, so
 * the next generation run produces the subscription's definition again, as if the edit had
 * never happened. That is the point of the feature, not a limitation of it.
 *
 * `planName`, `chabadHouseName`, `orderDate` and the plan itself are absent on purpose: they
 * identify the shipment rather than describe it, and an order that can be reassigned to another
 * customer or another week is no longer a record of anything.
 */
export interface UpdateOrderBody {
  deliveryMethod: string;
  /** Cleared by the server whenever the method is not משלוח עד הבית, as on the subscription. */
  shippingCompany?: string | null;
  pickupPointId?: string | null;
  /** Another order of the same day and plan — see `SubscriptionOrder.joinsOrderId`. */
  joinsOrderId?: number | null;
  address?: Partial<SubscriptionAddress>;
  /** Only read when `deliveryCostOverridden` is true; derived from the method otherwise. */
  deliveryCost?: number;
  deliveryCostOverridden?: boolean;
  items: UpdateOrderItem[];
}

// --- תצוגת מחסן (packing queue) ---

/**
 * One row of the warehouse packing queue: an order, plus the few things the packer needs that
 * the order itself cannot answer.
 */
export interface PackingQueueOrder extends SubscriptionOrder {
  /**
   * Who to call about this package. `address.recipientPhone` is filled only for משלוח עד הבית,
   * so this falls back to the customer's **current** phone number.
   *
   * A deliberate exception to the snapshot rule at the top of this file: the packer is calling
   * today, and a number frozen two weeks ago is the wrong number to dial. Nothing else on the
   * screen reads live data.
   */
  contactPhone: string | null;
  /** True when this order rides along with another one, i.e. it is placed under a parent. */
  isJoiner: boolean;
  /** How many orders join *this* one. 0 for everything except a parent shipment. */
  joinerCount: number;
  /**
   * The subscription's tags **as they are now**, by name — the second live exception on this
   * screen, for the same reason as the phone: a tag is a packing instruction, and the packer
   * needs today's. Empty for an order whose subscription was deleted.
   */
  tags: SubscriptionTag[];
  /**
   * The subscription's remembered box count, live — the third exception, so a count the packer
   * gave last month starts this month's dialog. Null when it is not known yet (a new subscription,
   * or one whose content changed): the dialog then starts at one and the answer is remembered.
   */
  defaultPackagesCount: number | null;
  /**
   * Already packed (`הזמנה הושלמה`). Packed orders stay in the queue, struck through, rather than
   * disappearing: the packer can still open one and reprint its label — without a new shipment.
   */
  packed: boolean;
}

export interface PackingQueueResponse {
  /**
   * The shipping day being packed — the most recent `orderDate` that still has הזמנה חדשה
   * orders, or simply the most recent one when everything is already packed.
   * Null when no orders exist at all.
   */
  orderDate: string | null;
  /**
   * The day's orders in packing order — those still to pack (`הזמנה חדשה`) and those already
   * packed (`הזמנה הושלמה`, `packed: true`) in the same sequence, so nothing renumbers as the
   * day goes on.
   */
  queue: PackingQueueOrder[];
  /** Orders of the same day already moved to `הזמנה הושלמה` — the "ארוזות" counter. */
  packedCount: number;
}

export interface SubscriptionOrderListResponse {
  rows: SubscriptionOrder[];
  count: number;
}

export interface GenerateOrdersBody {
  planId: number;
}

export interface GenerateOrdersResult {
  batchId: number;
  scannedCount: number;
  orderCount: number;
  skippedCount: number;
}

/** What the confirm dialog shows before committing to a run. */
export interface OrderGenerationPreflight {
  planId: number;
  planName: string;
  /** The plan's own settings, so the dialog can say how this plan is meant to be generated. */
  planFrequency: string;
  planOrderCreation: string;
  activePlanItems: number;
  activeSubscriptions: number;
  /** Subscriptions that would produce an order. */
  willCreate: number;
  /** Subscriptions that would be skipped for having no active items. */
  willSkip: number;
  /** Set when this plan was already generated today, so the dialog can warn about a repeat. */
  lastBatchToday: { id: number; generatedAt: string; orderCount: number } | null;
}

// --- הפקת מדבקה ---

/**
 * The pseudo-carrier for a package nobody ships: איסוף עצמי, נקודת איסוף, and a joiner that
 * travels inside somebody else's box. There is no external system to open a shipment at, so
 * the label is one we draw ourselves — but the packer's flow, the box count and the printed
 * document are identical, which is why it is modelled as a provider rather than as an
 * exception scattered through the code.
 */
export const SHIPMENT_PROVIDER_INTERNAL = 'פנימי';

/**
 * The training switch for the carriers' APIs (`GET|PUT /shipping/api-switch`). Off, nothing is
 * sent to כץ or שיפינג: every package is opened on the internal provider and the packing flow runs
 * exactly as it does for real, so a training session never books a real pickup.
 */
export interface ShippingApiSwitch {
  enabled: boolean;
  /** When it was last flipped and by whom; null while it has never been touched (the default, on). */
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * A shipment opened for one order.
 *
 * Existence of a non-cancelled row is what makes printing idempotent: pressing
 * הדפס מדבקה again reprints the same shipment instead of opening a second one at
 * the carrier. A package is a physical object and can only be sent once.
 */
export interface OrderShipment {
  id: number;
  orderId: number;
  /** One of `SHIPPING_COMPANIES`, or `SHIPMENT_PROVIDER_INTERNAL`. */
  provider: string;
  packagesCount: number;
  /** The carrier's own shipment number. Null for the internal provider. */
  carrierShipmentId: string | null;
  carrierBarcode: string | null;
  trackingUrl: string | null;
  createdAt: string;
  cancelledAt: string | null;
}

/** `POST /subscription-orders/:id/ship` — how many boxes the packer actually filled. */
export interface CreateOrderShipmentBody {
  packagesCount: number;
}

/**
 * The bounds on the box-count prompt.
 *
 * The upper bound is not a carrier limit — כץ answers `1200 כמות אריזות הלוך גדולה מהמותר`
 * on its own, and per account. It is here to catch a typed `11` that meant `1`, before it
 * becomes eleven labels and a shipment somebody has to phone in to cancel.
 */
export const MIN_PACKAGES_PER_ORDER = 1;
export const MAX_PACKAGES_PER_ORDER = 20;

export const PACKAGES_PROMPT_TITLE = 'בכמה ארגזים נארזה ההזמנה?';
export const PACKAGES_PROMPT_HELPER = 'תודפס מדבקה לכל ארגז, בנוסף לתעודת המשלוח.';
